import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, getFeaturedArticles } from '@/mocks/articles';

const faqData = [
  {
    question: '催眠疗法安全吗？会不会被控制？',
    answer: '催眠疗法是一种安全、科学的心理治疗工具。在催眠状态下，你始终保持着完整的意识和自我控制能力，不会被强迫做任何你不愿意的事情。催眠是一种高度专注和深度放松的自然状态，类似于我们日常中沉浸在一本好书或一部电影中的体验。专业的催眠治疗师遵守严格的职业道德规范，整个过程由你主导。',
  },
  {
    question: '催眠改善亲密关系需要多长时间才能见效？',
    answer: '效果的显现因人而异。一些人可能在第一次催眠疗愈后就感受到明显的变化，如身体更加放松、情绪更加平稳。对于深层的关系模式改变，通常需要4-8次的系统疗愈。催眠不是魔法，而是一个渐进的、累积的过程。许多来访者反映，在三次疗愈后，他们和伴侣之间的沟通质量和情感连接有了显著改善。',
  },
  {
    question: '自我催眠和专业催眠治疗有什么区别？',
    answer: '自我催眠是一种日常的自我调节工具，适合减压放松、提升专注力、改善睡眠等日常需求。专业催眠治疗则由经过系统培训的催眠治疗师进行，针对特定的心理困扰（如焦虑症、创伤疗愈、关系问题）提供深度干预。两者是互补关系——专业治疗帮助你识别和解决深层问题，自我催眠帮助你巩固和延续治疗效果。',
  },
  {
    question: '什么样的人不适合接受催眠治疗？',
    answer: '催眠治疗对大多数人来说都是安全的，但以下人群需要谨慎或在医生指导下进行：严重精神疾病患者（如精神分裂症）、癫痫患者、以及某些人格障碍患者。在开始催眠治疗前，专业的催眠治疗师会进行详细的评估，确保催眠是适合你的治疗方式。如果你有任何健康方面的顾虑，建议先咨询你的医生。',
  },
  {
    question: '催眠能帮助解决哪些具体的亲密关系问题？',
    answer: '催眠疗法可以帮助解决多种亲密关系问题，包括但不限于：沟通障碍与情感表达困难、性焦虑与性功能障碍的心理层面、信任问题与依恋创伤、亲密关系中的重复性冲突模式、分手或离婚后的情感修复、以及因过往创伤导致的亲密恐惧。催眠通过深入潜意识，从根源上松动那些限制性的信念和情绪模式。',
  },
];

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const featuredArticles = getFeaturedArticles(6);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '催眠性福生活',
    url: 'https://www.cuimianxingfu.com',
    description: '探索催眠带来的性福生活，了解催眠如何帮助释放心理障碍、提升亲密关系质量、增强身心愉悦感。',
    email: 'contact@cuimianxingfu.com',
    telephone: '400-888-9999',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '北京',
      streetAddress: '朝阳区建国路88号',
      addressCountry: 'CN',
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '催眠性福生活',
    url: 'https://www.cuimianxingfu.com',
    description: '探索催眠带来的性福生活，了解催眠如何帮助释放心理障碍、提升亲密关系质量、增强身心愉悦感。',
    inLanguage: 'zh-Hans',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SchemaOrg data={organizationSchema} />
      <SchemaOrg data={websiteSchema} />
      <SchemaOrg data={faqSchema} />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20warm%20golden%20and%20cream%20flowing%20waves%20with%20soft%20organic%20shapes%2C%20ethereal%20atmosphere%2C%20healing%20and%20spiritual%20vibe%2C%20minimalist%20abstract%20art%2C%20soft%20gradients%2C%20no%20people%2C%20warm%20color%20palette%20of%20amber%20rose%20and%20cream&width=1600&height=900&seq=hero-bg-2026&orientation=landscape"
                alt="催眠带来性福生活 - 用潜意识的力量重塑亲密关系"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
            </div>
            <div className="relative z-10 w-full px-4 md:px-6 text-center">
              <h1 className="text-2xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 md:mb-6 leading-tight">
                催眠带来的性福生活
              </h1>
              <p className="text-sm md:text-lg text-white/85 max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
                用潜意识的力量，释放心理障碍<br className="hidden md:block" />
                重塑亲密关系与幸福感，开启更满足的生活方式
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/hypnosis-improve-intimacy"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
                >
                  探索催眠疗愈
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/article/self-hypnosis-beginners-guide"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/20 hover:bg-background-50/30 backdrop-blur-sm text-white border border-white/30 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
                >
                  学习自我催眠
                  <i className="ri-book-read-line"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* Category Grid Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  探索催眠疗愈领域
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  从亲密关系到心理健康，从压力管理到自我成长——找到适合你的催眠疗愈方向
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="group relative block overflow-hidden rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-base md:text-lg font-heading font-semibold text-white">
                          {cat.name}
                        </h3>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {cat.description}
                      </p>
                      <span className="inline-flex items-center gap-1 mt-3 text-xs text-primary-600 font-medium whitespace-nowrap">
                        共 {cat.articleCount} 篇文章
                        <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100/70">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-end justify-between mb-8 md:mb-10">
                <div>
                  <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    精选文章
                  </h2>
                  <p className="text-sm text-foreground-500">
                    专业催眠治疗师撰写的深度文章，带你了解催眠的科学与艺术
                  </p>
                </div>
                <Link
                  to="/category/hypnosis-improve-intimacy"
                  className="hidden md:inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {featuredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group block bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all overflow-hidden cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-1.5 text-xs text-foreground-400 mb-2 flex-wrap">
                        <span className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full text-xs whitespace-nowrap">
                          {article.category}
                        </span>
                        <span>{article.date}</span>
                        <span>·</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-6 text-center md:hidden">
                <Link
                  to="/category/hypnosis-improve-intimacy"
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
                >
                  查看全部文章
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* Why Hypnosis Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  为什么选择催眠疗愈
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  催眠不是神秘魔法，而是经过科学验证的心理治疗工具
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {[
                  {
                    icon: 'ri-brain-line',
                    title: '深入潜意识',
                    desc: '绕过意识的防御机制，直达问题的根源所在，实现深层而持久的改变。',
                  },
                  {
                    icon: 'ri-heart-pulse-line',
                    title: '温和无创',
                    desc: '无需药物、无需侵入性治疗，通过自然的放松和暗示引导实现身心疗愈。',
                  },
                  {
                    icon: 'ri-speed-up-line',
                    title: '见效迅速',
                    desc: '相比传统的谈话治疗，催眠疗法通常在更少的疗程内就能取得显著效果。',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '科学验证',
                    desc: '大量神经科学研究和临床实验证实了催眠疗法在多个领域的有效性。',
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/70 text-center"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-full">
                      <i className={`${item.icon} text-xl text-primary-600`}></i>
                    </div>
                    <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100/70">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">
                  关于催眠疗愈，你可能想知道的一切
                </p>
              </div>
              <div className="space-y-3">
                {faqData.map((faq, i) => (
                  <div
                    key={i}
                    className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                    >
                      <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                        {faq.question}
                      </span>
                      {openFaq === i ? (
                        <i className="ri-arrow-up-s-line text-foreground-400 transition-transform flex-shrink-0"></i>
                      ) : (
                        <i className="ri-arrow-down-s-line text-foreground-400 transition-transform flex-shrink-0"></i>
                      )}
                    </button>
                    {openFaq === i && (
                      <div className="px-4 md:px-5 pb-4 md:pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative py-14 md:py-20 px-4 md:px-6 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Soft%20abstract%20organic%20flowing%20shapes%20in%20warm%20amber%20rose%20and%20cream%20tones%2C%20ethereal%20and%20healing%20atmosphere%2C%20gentle%20gradients%2C%20minimalist%20abstract%20art%20with%20no%20people&width=1600&height=700&seq=cta-bg-2026&orientation=landscape"
                alt="开启催眠疗愈之旅"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/35 to-black/50"></div>
            </div>
            <div className="relative z-10 max-w-2xl mx-auto text-center">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-white mb-3">
                准备好开启你的幸福之旅了吗？
              </h2>
              <p className="text-sm md:text-base text-white/80 mb-6 md:mb-8 leading-relaxed">
                催眠疗愈为你打开一扇通往内心深处的大门，释放被压抑的潜能，拥抱更丰盛、更满足的生活。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/self-hypnosis-techniques"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
                >
                  开始学习自我催眠
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/20 hover:bg-background-50/30 backdrop-blur-sm text-white border border-white/30 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
                >
                  了解更多
                  <i className="ri-information-line"></i>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}